/**
 * First-party storefront analytics client. Consent-gated, batched, lossy-by-design.
 * Lead counts NEVER depend on this. Events: page_view, popup_*, form_view, form_start.
 * (No form_submit — the submission row is the conversion; join via session_id.)
 */
import { CONSENT_UPDATED_EVENT } from '@/lib/consent'
import { privacyAllows } from '@/lib/privacyRuntime'
import { classifyReferrerSource } from '@sifly/shared-utils'

const ENDPOINT = '/api/analytics/event'
const SESSION_KEY = 'sifly_an_session'
// Session flag: the first-touch referrer source has been recorded this session.
const REF_KEY = 'sifly_an_ref'
const CLICK_KEY = 'sifly_an_last_click'
const CLICK_TTL = 30 * 60_000
const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i
const FLUSH_AT = 10

/**
 * Returns the existing analytics session id WITHOUT creating one.
 * Returns null when:
 *   - running server-side (no sessionStorage)
 *   - consent was never granted (session was never started)
 *
 * Use this when you want to JOIN to analytics events but MUST NOT
 * mint a new session for a visitor who hasn't consented.
 */
export function peekSessionId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    // Accessing window.sessionStorage throws a SecurityError in third-party
    // iframe contexts with blocked site data. This runs during RENDER
    // (AnalyticsFormRender's useMemo), so an unguarded throw here crashed
    // hydration for any embedded storefront page (found 2026-07-04).
    return sessionStorage.getItem(SESSION_KEY)
  } catch {
    return null
  }
}

export type BookingAttribution = {
  sessionId: string
  lastClick?: { eventId: string; pagePath: string; href: string; label: string; clickedAt: string }
}

// Persist public paths only: never copy URL queries, fragments or private
// booking-management tokens into the durable booking attribution snapshot.
function publicPath(value: unknown, pagePath = '/'): string | undefined {
  if (typeof value !== 'string' || (value.includes('\\') || /\p{Cc}/u.test(value)) || value.startsWith('//')) return
  try {
    const url = new URL(value, new URL(pagePath, location.origin))
    if (url.origin !== location.origin) return
    const path = url.pathname
    const decoded = decodeURIComponent(path)
    if (path.length > 300 || decoded.startsWith('//') || (decoded.includes('\\') || /[?#\p{Cc}]/u.test(decoded)) || /%[0-9a-f]{2}/i.test(decoded)) return
    // Decoding a separator can expose traversal that URL did not normalize on
    // the first pass. Reject layered escapes and check the canonical path.
    const normalized = new URL(decoded, location.origin).pathname
    if (normalized.length > 300) return
    if (/^\/(?:api|account|checkout|booking|manage|flow|waiver|payment|quote|order|preview|r)(?:\/|$)/i.test(normalized)) return
    if (/^\/bookings\/(?:flow|manage)(?:\/|$)/i.test(normalized)) return
    return normalized
  } catch { return }
}

/** Read an existing, permitted journey; never mint a session for checkout. */
export function getBookingAttribution(): BookingAttribution | undefined {
  if (typeof window === 'undefined' || !consentGranted()) return
  const sessionId = peekSessionId()
  if (!sessionId || !UUID.test(sessionId)) return
  const result: BookingAttribution = { sessionId }
  try {
    const raw = sessionStorage.getItem(CLICK_KEY)
    if (!raw) return result
    const saved = JSON.parse(raw) as BookingAttribution
    const click = saved.lastClick
    if (saved.sessionId !== sessionId || !click || !UUID.test(click.eventId)) return result
    const age = Date.now() - Date.parse(click.clickedAt)
    if (!Number.isFinite(age) || age < -60_000 || age > CLICK_TTL) return result
    const pagePath = publicPath(click.pagePath)
    const href = publicPath(click.href)
    if (!pagePath || !href || typeof click.label !== 'string' || !click.label.trim() || click.label.length > 120) return result
    result.lastClick = { eventId: click.eventId, pagePath, href, label: click.label.trim(), clickedAt: click.clickedAt }
  } catch { /* browser storage is optional; the booking must still work */ }
  return result
}

function rememberClick(event: AnalyticsEvent) {
  try {
    const pagePath = publicPath(event.page_path)
    const href = publicPath(event.metadata?.href, pagePath)
    const label = event.metadata?.label?.trim().slice(0, 120)
    sessionStorage.removeItem(CLICK_KEY)
    if (!pagePath || !href || !label) return
    sessionStorage.setItem(CLICK_KEY, JSON.stringify({ sessionId: event.session_id, lastClick: {
      eventId: event.event_id, pagePath, href, label, clickedAt: new Date().toISOString(),
    } }))
  } catch { /* attribution must never block the click */ }
}

type AnalyticsEvent = {
  event_id: string; event_type: string; session_id: string
  page_path?: string; metadata?: Record<string, string>
}
let buffer: AnalyticsEvent[] = []
let initialized = false

// In-memory fallback when sessionStorage is blocked (embedded contexts):
// keeps one stable session id per page view so events still group, without
// crashing. Not persisted — that's acceptable for lossy-by-design analytics.
let memSessionId: string | null = null

function sessionId(): string {
  try {
    let s = sessionStorage.getItem(SESSION_KEY)
    if (!s) { s = crypto.randomUUID(); sessionStorage.setItem(SESSION_KEY, s) }
    return s
  } catch {
    if (!memSessionId) memSessionId = crypto.randomUUID()
    return memSessionId
  }
}
// The tenant comes from the privacy runtime's own config (the layout's
// `data-sf-privacy`), so this answer does not depend on initAnalytics having
// run first.
function consentGranted(): boolean {
  return privacyAllows('analytics')
}

// Record the traffic source of the session's FIRST page once, into that page's
// metadata as `referrer_source`. document.referrer is the external origin on
// the landing page (internal navigations classify to null and are skipped), so
// the first page_view of a session carries the true first-touch source. The
// session flag dedupes across the session's subsequent page loads; a module var
// covers embedded contexts where sessionStorage throws.
let refRecorded = false
function firstTouchSource(): string | null {
  if (refRecorded) return null
  try {
    if (sessionStorage.getItem(REF_KEY)) { refRecorded = true; return null }
  } catch { /* storage blocked — rely on the module var below */ }
  refRecorded = true
  const source = classifyReferrerSource(document.referrer, location.host)
  try { sessionStorage.setItem(REF_KEY, source ?? '-') } catch { /* ignore */ }
  return source
}

export function track(event_type: string, metadata: Record<string, string> = {}, page_path?: string) {
  if (typeof window === 'undefined') return
  if (!consentGranted()) return
  const meta = { ...metadata }
  if (event_type === 'page_view') {
    const source = firstTouchSource()
    if (source) meta.referrer_source = source
  }
  const event: AnalyticsEvent = {
    event_id: crypto.randomUUID(),
    event_type, session_id: sessionId(),
    page_path: page_path ?? location.pathname, metadata: meta,
  }
  buffer.push(event)
  if (event_type === 'section_cta_click') rememberClick(event)
  if (buffer.length >= FLUSH_AT) flush()
}
function flush() {
  if (buffer.length === 0) return
  const batch = buffer
  buffer = []
  const body = JSON.stringify({ events: batch })
  const ok = typeof navigator.sendBeacon === 'function'
    && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }))
  if (!ok) { void fetch(ENDPOINT, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(() => {}) }
}
// `_tenantId` is unused since the privacy runtime reads the tenant itself
// (2026-10); callers still pass it and still need the listeners below.
export function initAnalytics(_tenantId?: string) {
  if (typeof window === 'undefined') return
  if (!initialized) {
    initialized = true
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush() })
    window.addEventListener(CONSENT_UPDATED_EVENT, () => {
      if (!consentGranted()) {
        buffer = []
        // Also drop the session id: a post-revocation form submission must not
        // carry a stale session (it would mis-record consent_state='granted' via
        // peekSessionId, and shouldn't join the now-withdrawn analytics journey).
        memSessionId = null
        // Re-arm first-touch capture so a fresh post-regrant session re-records
        // its referrer source instead of inheriting the withdrawn one.
        refRecorded = false
        try {
          sessionStorage.removeItem(SESSION_KEY)
          sessionStorage.removeItem(REF_KEY)
          sessionStorage.removeItem(CLICK_KEY)
        } catch {
          // storage blocked (embedded context) — nothing persisted to drop
        }
      }
    })
  }
}
export const __test = {
  track,
  reset: () => { buffer = []; initialized = false; refRecorded = false },
  pendingCount: () => buffer.length,
  peek: () => buffer,
  sessionId,
  flush,
}
